import type { Unsubscribe } from '@amazon/vinyl-util'
import {
    createDisposer,
    getSearchParams,
    logDebug,
    logWarn,
    noop,
    requestWithRetry,
} from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { cleanCache, getCacheManager } from '@/offline/offlineCacheManager'
import { ctx } from '@/util/context'
import { AuthStatus } from '@/auth/auth'

const { target, errorHandler } = ctx('offline')

export const cacheEnabled = !getSearchParams().has('noCache')

export function initializeOfflineCache(): Unsubscribe {
    const { add, dispose } = createDisposer()
    const cacheManager = getCacheManager()
    if (cacheEnabled) {
        logDebug(target, 'caching enabled')
    } else {
        logDebug(target, 'clearing offline cache...')
        cacheManager.clear().catch(noop)
    }

    setTimeout(() => {
        // Purge the stale items from the cache, but not while the app is starting.
        cleanCache().catch(errorHandler)
    }, 5 * 1000)

    add(
        getAuth().status.onData((_, previousValue) => {
            // The customer signed out, clear all offline cache data.
            if (previousValue === AuthStatus.SIGNED_IN) {
                logDebug(target, 'clearing cache after sign-out')
                // User has changed; cached queries should be cleared.
                cacheManager.clear().catch(errorHandler)
            }
        })
    )

    return dispose
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function initializeServiceWorker() {
    if ('serviceWorker' in navigator) {
        logDebug(target, 'Initializing service worker')
        const registration = await navigator.serviceWorker
            .register(`/serviceWorker.js`, { scope: '/' })
            .catch((error) => {
                logWarn(target, 'Could not register service worker:', error)
            })
        if (registration) {
            if (registration.installing) {
                logDebug(target, 'Service worker installing')
            } else if (registration.waiting) {
                logDebug(target, 'Service worker installed')
            } else if (registration.active) {
                logDebug(target, 'Service worker active')
                await registration.update().catch((error) => {
                    logWarn(target, 'Could not update service worker:', error)
                })
            }
        }
    }
}

/**
 * Takes a list of critical files and caches them for offline support.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function cacheOfflineFiles() {
    logDebug(target, 'Caching offline files')
    const cacheManager = getCacheManager()
    const manifest = await (await requestWithRetry('./manifest.txt')).text()
    const urlsToCache = manifest.split('\n').concat('/')
    // Load each file needed for offline support serially in order to avoid overloading a frame.
    for (const url of urlsToCache) {
        await cacheManager.get(url).catch(errorHandler)
    }
}
